<template>
  <dx-modal
    v-if="show"
    :value="show"
    @input="$emit('close')"
    :title="null"
    size="md"
    class="license-confirmation-modal"
  >
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="header-title">Confirmar Alteração de Licenças</h3>
      </div>

      <div class="modal-body">
        <div class="comparison-section">
          <div class="comparison-item">
            <div class="comparison-label">Atual</div>
            <div class="comparison-value">
              <div class="value-number">{{ currentQuantity }}</div>
              <div class="value-text">licenças</div>
            </div>
            <div class="comparison-price">{{ currentPrice | currency }}/mês</div>
          </div>

          <div class="comparison-arrow">
            <fa-icon :icon="['fal', 'arrow-right']" />
          </div>

          <div class="comparison-item highlighted">
            <div class="comparison-label">Novo</div>
            <div class="comparison-value">
              <div class="value-number">{{ newQuantity }}</div>
              <div class="value-text">licenças</div>
            </div>
            <div class="comparison-price">{{ newPrice | currency }}/mês</div>
          </div>
        </div>

        <div
          v-if="priceDifference !== 0"
          class="impact-badge"
          :class="priceDifference > 0 ? 'is-increase' : 'is-decrease'"
        >
          <fa-icon
            :icon="['fal', priceDifference > 0 ? 'arrow-up' : 'arrow-down']"
          />
          <span>
            {{ priceDifference > 0 ? 'Aumento de' : 'Economia de' }}
            <strong>{{ Math.abs(priceDifference) | currency }}/mês</strong>
          </span>
        </div>

        <div class="alert-info">
          <fa-icon :icon="['fal', 'info-circle']" />
          <span>A alteração será aplicada imediatamente e refletida na próxima fatura.</span>
        </div>
      </div>
    </div>

    <template slot="footer">
      <button class="btn btn-primary mr-1"
              :class="{loading: loading}"
              :disabled="loading"
              @click="$emit('confirm')">
        Confirmar Alteração
      </button>
      <button class="btn" @click="$emit('close')">
        Cancelar
      </button>
    </template>
  </dx-modal>
</template>

<script>
export default {
  name: 'LicenseConfirmationModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentQuantity: {
      type: Number,
      required: true,
    },
    newQuantity: {
      type: Number,
      required: true,
    },
    unitPrice: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    currentPrice() {
      return this.currentQuantity * this.unitPrice;
    },
    newPrice() {
      return this.newQuantity * this.unitPrice;
    },
    priceDifference() {
      return this.newPrice - this.currentPrice;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.license-confirmation-modal {
  .modal-content {
    padding: 0;
  }

  .modal-header {
    padding: $layout-spacing-lg * 1.5 $layout-spacing-lg * 2;
    border-bottom: 1px solid $border-color;
    background: $bg-color-light;

    .header-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: $dark-color;
    }
  }

  .modal-body {
    padding: $layout-spacing-lg * 2;
  }

  .comparison-section {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: $layout-spacing-lg * 1.5;
    align-items: center;
    margin-bottom: $layout-spacing-lg * 1.5;

    @media (max-width: $size-sm) {
      grid-template-columns: 1fr;
      gap: $layout-spacing-lg;
    }

    .comparison-item {
      text-align: center;
      padding: $layout-spacing-lg * 1.5;
      background: $bg-color;
      border: 1px solid $border-color;
      border-radius: 10px;
      transition: all 0.3s ease;

      &.highlighted {
        background: linear-gradient(
          135deg,
          lighten($primary-color, 45%) 0%,
          lighten($primary-color, 48%) 100%
        );
        border-color: $primary-color;
        box-shadow: 0 2px 8px rgba($primary-color, 0.15);
      }

      .comparison-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: $gray-color-dark;
        margin-bottom: $layout-spacing-lg;
      }

      .comparison-value {
        margin-bottom: $layout-spacing;

        .value-number {
          font-size: 48px;
          font-weight: 800;
          color: $dark-color;
          line-height: 1;
          margin-bottom: $layout-spacing-sm;
        }

        .value-text {
          font-size: 13px;
          color: $gray-color-dark;
          font-weight: 600;
        }
      }

      .comparison-price {
        font-size: 16px;
        font-weight: 700;
        color: $dark-color;
      }
    }

    .comparison-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $primary-color;
      font-size: 24px;

      @media (max-width: $size-sm) {
        transform: rotate(90deg);
      }
    }
  }

  .impact-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $layout-spacing-sm;
    padding: $layout-spacing $layout-spacing-lg;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: $layout-spacing-lg * 1.5;

    &.is-increase {
      background: lighten($error-color, 45%);
      color: darken($error-color, 15%);
      border: 1px solid lighten($error-color, 30%);
    }

    &.is-decrease {
      background: lighten($success-color, 45%);
      color: darken($success-color, 15%);
      border: 1px solid lighten($success-color, 30%);
    }

    strong {
      font-weight: 700;
    }
  }

  .alert-info {
    display: flex;
    align-items: center;
    gap: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    background: lighten($primary-color, 47%);
    border-left: 3px solid $primary-color;
    border-radius: 4px;
    font-size: 13px;
    color: darken($primary-color, 10%);
    line-height: 1.5;

    svg {
      flex-shrink: 0;
      color: $primary-color;
    }
  }
}
</style>
